import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { FocusHeader } from '../components/FocusHeader';
import { Calendar, Users } from 'lucide-react';

interface Campaign {
  id: string;
  subject: string;
  content: string;
  status: 'draft' | 'scheduled' | 'sending' | 'completed' | 'failed';
  scheduled_at: string | null;
  completed_at: string | null;
  total_contacts: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
  blog_id: string | null;
  user_id: string;
  from_name: string | null;
  recipient_filters?: { tags?: string[] } | null;
  published_blogs?: {
    title: string;
  };
}

export function CampaignDetail() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState<Campaign | null>(null);

  useEffect(() => {
    if (user && campaignId) {
      loadCampaign();
    } else {
      navigate('/login');
    }
  }, [user, campaignId, navigate]);

  const loadCampaign = async () => {
    if (!campaignId) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('email_campaigns')
        .select('*, published_blogs(title)')
        .eq('id', campaignId)
        .single();

      if (error) {
        console.error('Error loading campaign:', error);
      } else {
        setCampaign(data);
      }
    } catch (err) {
      console.error('Error loading campaign:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <FocusHeader
          title="Campaign laden..."
          onBack={() => navigate('/campaigns')}
        />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner text="Campaign laden..." />
        </main>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-gray-50">
        <FocusHeader
          title="Campaign Niet Gevonden"
          onBack={() => navigate('/campaigns')}
        />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="card text-center">
            <h2 className="text-2xl font-bold text-[#231F20] mb-4">
              Campaign Niet Gevonden
            </h2>
            <button onClick={() => navigate('/campaigns')} className="btn-primary">
              Terug naar Campaigns
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <FocusHeader
        title={campaign.subject}
        subtitle={campaign.published_blogs ? `Blog: ${campaign.published_blogs.title}` : undefined}
        onBack={() => navigate('/campaigns')}
      />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <div className="card space-y-3">
            {(campaign.recipient_filters?.tags?.length ?? 0) > 0 && (
              <div className="flex items-center gap-2 text-gray-700 text-lg">
                <Users className="w-5 h-5" />
                <span>
                  <span className="font-semibold">Doelgroep:</span>{' '}
                  {campaign.recipient_filters!.tags!.join(', ')}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 text-gray-700 text-lg">
              <Calendar className="w-5 h-5" />
              <span>
                {campaign.status === 'scheduled' && campaign.scheduled_at ? (
                  <>
                    <span className="font-semibold">Ingepland voor</span>{' '}
                    {new Date(campaign.scheduled_at).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}{' '}
                    om{' '}
                    {new Date(campaign.scheduled_at).toLocaleTimeString('nl-NL', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </>
                ) : (
                  <>
                    <span className="font-semibold">{campaign.sent_count}</span> emails verzonden
                    {campaign.completed_at && (
                      <span className="text-gray-500"> op {new Date(campaign.completed_at).toLocaleDateString('nl-NL', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}</span>
                    )}
                  </>
                )}
              </span>
            </div>
          </div>

          <div className="card space-y-4">
            <h2 className="text-xl font-bold text-[#231F20]">Email Preview</h2>
            <div
              className="border rounded-lg p-6 bg-white max-h-[600px] overflow-y-auto prose prose-sm max-w-none"
              style={{
                lineHeight: '1.6'
              }}
            >
              <style>{`
                .prose p { margin-bottom: 1em; }
                .prose p:last-child { margin-bottom: 0; }
              `}</style>
              <div dangerouslySetInnerHTML={{ __html: campaign.content }} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
